import React, { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from '@/lib/utils';

// Color palette options using complementary colors from the opposite side of the color wheel
// Avoiding red, green, and yellow entirely
const colorPalettes = {
  option1: {
    name: "Royal & Serene",
    description: "Deep purples and blues for a professional look",
    primary: "#6E59A5", // Deep purple
    secondary: "#0EA5E9", // Ocean blue
    accent: "#9b87f5", // Soft lavender
    neutral: "#1A1F2C", // Deep navy
    background: "#F8F9FC", // Cool white
    headerBg: "#FFFFFF", // White
    navText: "#1A1F2C", // Deep navy
    navTextHover: "#6E59A5", // Deep purple
    buttonPrimary: "#6E59A5", // Deep purple
    buttonSecondary: "#0EA5E9", // Ocean blue
  },
  option2: {
    name: "Cool & Tranquil",
    description: "Calming blues and soft purples",
    primary: "#0EA5E9", // Ocean blue
    secondary: "#8B5CF6", // Vivid purple
    accent: "#D3E4FD", // Soft blue
    neutral: "#404352", // Slate gray
    background: "#F9FAFC", // Icy white
    headerBg: "#FFFFFF", // White
    navText: "#404352", // Slate gray
    navTextHover: "#0EA5E9", // Ocean blue
    buttonPrimary: "#0EA5E9", // Ocean blue
    buttonSecondary: "#8B5CF6", // Vivid purple
  },
  option3: {
    name: "Modern & Bold",
    description: "Bold purples with neutral accents",
    primary: "#8B5CF6", // Vivid purple 
    secondary: "#0EA5E9", // Ocean blue
    accent: "#D6BCFA", // Light purple
    neutral: "#2A2D3E", // Dark slate
    highlight: "#33C3F0", // Sky blue
    background: "#FFFFFF", // White
    headerBg: "#F8F8FC", // Very light purple tint
    navText: "#2A2D3E", // Dark slate
    navTextHover: "#8B5CF6", // Vivid purple
    buttonPrimary: "#8B5CF6", // Vivid purple
    buttonSecondary: "#0EA5E9", // Ocean blue
  },
};

// Mock header component to visualize different color schemes
const MockHeader = ({ colors }: { colors: any }) => {
  return (
    <div 
      className="py-4 w-full" 
      style={{ backgroundColor: colors.headerBg }}
    >
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <img src="/lovable-uploads/737eda11-ffdf-44eb-8c55-8e91b5323e2a.png" alt="Logo" className="h-8 w-auto" />
          <span className="font-bold text-lg" style={{ color: colors.navText }}>EmergencyPlan</span>
        </div>
        
        <div className="hidden md:flex space-x-6">
          {["Home", "Features", "Pricing", "About"].map((item) => (
            <a 
              key={item} 
              href="#" 
              className="transition-colors text-sm font-medium"
              style={{ 
                color: colors.navText,
              }}
            >
              {item}
            </a>
          ))}
        </div>
        
        <div className="hidden md:flex items-center space-x-4">
          <Button 
            variant="outline" 
            style={{ 
              borderColor: colors.buttonSecondary, 
              color: colors.buttonSecondary,
            }}
          >
            Log in
          </Button>
          <Button 
            style={{ 
              backgroundColor: colors.buttonPrimary,
              color: "#FFFFFF",
            }}
          >
            Get Free Plan
          </Button>
        </div>
      </div>
    </div>
  );
};

// Component to display color swatches
const ColorSwatch = ({ color, name }: { color: string; name: string }) => {
  return (
    <div className="flex flex-col items-center">
      <div 
        className="w-16 h-16 rounded-md mb-2 shadow-md" 
        style={{ backgroundColor: color }}
      ></div>
      <span className="text-xs font-medium">{name}</span>
      <span className="text-xs text-muted-foreground">{color}</span>
    </div>
  );
};

// Component to display various UI elements with the selected color scheme
const UIComponentsDisplay = ({ colors }: { colors: any }) => {
  return (
    <div className="space-y-8 mt-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Buttons</h3>
        <div className="flex flex-wrap gap-4">
          <Button 
            style={{ 
              backgroundColor: colors.buttonPrimary,
              color: "#FFFFFF",
            }}
          >
            Primary Button
          </Button>
          <Button 
            variant="outline"
            style={{ 
              borderColor: colors.buttonSecondary, 
              color: colors.buttonSecondary,
            }}
          >
            Secondary Button
          </Button>
          <Button 
            variant="ghost"
            style={{ color: colors.neutral || colors.navText }}
          >
            Ghost Button
          </Button>
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-4">Cards</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: colors.neutral || colors.navText }}>Card Title</CardTitle>
              <CardDescription>Card description goes here</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm">This is the content of the card.</p>
            </CardContent>
            <CardFooter>
              <Button 
                size="sm"
                style={{ 
                  backgroundColor: colors.buttonPrimary,
                  color: "#FFFFFF",
                }}
              >
                Action
              </Button>
            </CardFooter>
          </Card>
          
          <Card style={{ borderColor: colors.secondary, borderWidth: '1px' }}>
            <CardHeader>
              <CardTitle style={{ color: colors.secondary }}>Highlighted Card</CardTitle>
              <CardDescription>Special card with accent</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm">This card has a colored border.</p>
            </CardContent>
            <CardFooter>
              <Button 
                size="sm"
                style={{ 
                  backgroundColor: colors.secondary,
                  color: "#FFFFFF",
                }}
              >
                Special Action
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-4">Badges</h3>
        <div className="flex flex-wrap gap-2">
          <Badge style={{ backgroundColor: colors.primary, color: "#FFFFFF" }}>
            Primary
          </Badge>
          <Badge style={{ backgroundColor: colors.secondary, color: "#FFFFFF" }}>
            Secondary
          </Badge>
          <Badge style={{ backgroundColor: colors.accent, color: "#FFFFFF" }}>
            Accent
          </Badge>
          <Badge variant="outline" style={{ borderColor: colors.primary, color: colors.primary }}>
            Outline
          </Badge>
        </div>
      </div>
    </div>
  );
};

const Branding = () => {
  const [selectedPalette, setSelectedPalette] = useState<string>("option1");
  
  const colors = colorPalettes[selectedPalette as keyof typeof colorPalettes];
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 mt-20">
        <h1 className="text-3xl font-bold mb-2">Admin - Branding & Color Palette Options</h1>
        <p className="text-muted-foreground mb-8">
          Preview different color schemes based on the new logo. This page is not accessible from the public navigation.
        </p>
        
        <div className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Logo Reference</h2>
          <div className="p-6 bg-white rounded-lg shadow-sm border border-border">
            <img 
              src="/lovable-uploads/737eda11-ffdf-44eb-8c55-8e91b5323e2a.png" 
              alt="Logo Reference" 
              className="max-w-[300px] mx-auto"
            />
          </div>
        </div>
        
        <Tabs defaultValue="option1" className="w-full" onValueChange={setSelectedPalette}>
          <TabsList className="grid grid-cols-3 mb-8">
            <TabsTrigger value="option1">Royal & Serene</TabsTrigger>
            <TabsTrigger value="option2">Cool & Tranquil</TabsTrigger>
            <TabsTrigger value="option3">Modern & Bold</TabsTrigger>
          </TabsList>
          
          {Object.entries(colorPalettes).map(([key, palette]) => (
            <TabsContent key={key} value={key} className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>{palette.name}</CardTitle>
                  <CardDescription>{palette.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-8">
                    <ColorSwatch color={palette.primary} name="Primary" />
                    <ColorSwatch color={palette.secondary} name="Secondary" />
                    <ColorSwatch color={palette.accent} name="Accent" />
                    <ColorSwatch color={palette.neutral || (palette as any).highlight} name={(palette as any).highlight ? "Highlight" : "Neutral"} />
                    <ColorSwatch color={palette.background} name="Background" />
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden mb-8">
                    <h3 className="text-lg font-medium p-4 bg-muted">Header Preview</h3>
                    <div className="p-0">
                      <MockHeader colors={palette} />
                    </div>
                  </div>
                  
                  <UIComponentsDisplay colors={palette} />
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
        
        <div className="mt-12 p-6 border border-dashed rounded-lg">
          <h2 className="text-xl font-bold mb-4">Implementation Notes</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>These color schemes use complementary colors from the opposite side of the color wheel</li>
            <li>Purples and blues create contrast with the logo's red, green, and orange</li>
            <li>A light header background allows the colorful logo to stand out effectively</li>
            <li>These colors can be integrated into the CSS system using Tailwind config variables</li>
            <li>Consider using the accent color sparingly for important elements or calls to action</li>
            <li>Mobile responsiveness will be maintained across all color schemes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Branding;
